// #region Imports
import { Context, Dispatch, ReactNode } from 'react';
import { ActionMap, GoMotoFunction } from '../../API/GoMoto.types';
import { IAppointment } from '../../API/GoMoto';
import { AppointmentType, DateRangeKey } from '../../API/Enums';
import { TabType } from '../../controls/SearchGrid/SearchGrid/SearchGridAppointmentTile/SearchGridAppointmentTile.types';
import { Dealer, Kiosk } from '../Kiosk/kiosk.types';
// #endregion

// #region Setup
/**
 * A union type of statuses the `AdminFilters` can be set to.
 */
export type Status = 'initialize' | 'search';

/**
 * An object of filters to use to filter to specific appointments when we make API calls to retrieve appointments.
 */
export type AppointmentFilters = {
    /**
     * URL Key for the Kiosk.
     */
    kioskKey: Kiosk['key'];
    /**
     * URL Key for the Dealer.
     */
    dealerKey?: Dealer['key'];
    /**
     * The name of a user in an appointment to filter to.
     */
    name?: string;
    /**
     * When true, filter to appointments that were admin created.
     */
    adminCreated?: boolean;
    /**
     * When true, filter to appointments that have do not have something stored in a key slot.
     */
    keySlotEmpty?: boolean;
    /**
     * The starting date to filter to appointments within a specific range.
     */
    start?: Date;
    /**
     * The ending date to filter to appointments within a specific range.
     */
    end?: Date;
    /**
     * When true,filter to appointments with a key stored by the advisor.
     */
    storedByAdvisor?: boolean;
    /**
     * An array of key statuses to filter to.
     */
    keyStatus?: string[];
    /**
     * The session ID being used for the current kiosk admin login.
     */
    sessionId?: string;
};

/**
 * The date range object for filtering based on a specific range of dates.
 */
export interface FilterDateRange {
    /**
     * The date range type to use when filtering. i.e. today, yesterday, week, etc.
     */
    key: DateRangeKey;
    /**
     * The starting date to use in the date range filter.
     */
    start: Date;
    /**
     * The ending date to use in the date range filter. When undefined, filter to all appointments
     * after the start date.
     */
    end?: Date;
}

/**
 * The filter values stored after the filters are changed.
 */
export interface FilterValues {
    /**
     * The appointment tile that is currently filtered to by the autocomplete control in the filters.
     */
    appointment?: IAppointment | null;
    /**
     * The type of appointment tiles to filter to.
     */
    type?: AppointmentType;
    /**
     * The date to filter appointment tiles to.
     */
    date?: Date;
    /**
     * The date range type or range of dates to filter appointment tiles to.
     */
    dateRange?: FilterDateRange;
}

/**
 * An object of all appointment types to retrieve for displaying as specific appointment tiles.
 */
export interface AppointmentTypeCollection {
    /**
     * An array of all active appointments that are stored or checked in.
     */
    active: IAppointment[];
    /**
     * An array of all priority appointments.
     */
    priority: IAppointment[];
    /**
     * An array of all appointments from the DMS.
     */
    dms: IAppointment[];
}

/**
 * The object type that is maintained by the admin context and can be
 * consumed by any child of the admin provider.
 */
export interface AdminFiltersContextState {
    /**
     * What status the AdminFiltersContext is in. This is used to determine
     * if filter changes apply yet since we retrieve appointments and then filter
     * based on the appointments retrieved.
     */
    status?: Status;
    /**
     * An object of all the appointment filters used to filter appointments when we make
     * an API call to retrieve them from the back-end.
     */
    appointmentFilters: AppointmentFilters;
    /**
     * An object of all appointment filters used to filter appointment tiles already retrieved from
     * the back-end to a specific subset of appointment tiles.
     */
    filterValues?: FilterValues;
    /**
     * A collection of arrays of each type of appointment retrieved from the back-end after an API call.
     */
    appointments: AppointmentTypeCollection;
    /**
     * A single array of all the appointments retrieved from the back-end.
     */
    results: IAppointment[] | null;
    /**
     * When true, we have priority appointments.
     */
    hasPriorityAppointments: boolean;
}
// #endregion

/**
 * The type for the dispatch provider as well as the dispatch function itself.
 */
export type AdminFiltersDispatchState = Dispatch<AdminFiltersActions>;

/**
 * The context type for the admin state context. It can be undefined so that
 * we can verify anything attempting to consume the context is a child of the context.
 */
export type AdminFiltersState = AdminFiltersContextState | undefined;

/**
 * The context type for the admin dispatch context. It can be undefined so that
 * we can verify anything attempting to use the dispatch is a child of the context.
 */
export type AdminFiltersDispatch = AdminFiltersDispatchState | undefined;

/**
 * The admin state context type that is returned after the creation of the state context.
 */
export type AdminFiltersStateContextType = Context<AdminFiltersState>;

/**
 * The admin dispatch context type that is returned after the creation of the dispatch context.
 */
export type AdminFiltersDispatchContextType = Context<AdminFiltersDispatch>;
// #endregion

// #region Reducer
/**
 * The admin dispatch types used by our {@link ActionMap} type to distinguish what payload the
 * dispatch action will require.
 */
export enum AdminFiltersDispatchTypes {
    SetFilters = 'SET_FILTERS',
    SetAppointments = 'SET_APPOINTMENTS',
    Reinitialize = 'REINITIALIZE',
}

/**
 * An object mapping each dispatch type to the payload required for that dispatch action.
 */
export interface AdminFiltersPayload {
    [AdminFiltersDispatchTypes.SetFilters]: Pick<AdminFiltersContextState, 'appointmentFilters' | 'filterValues'>;
    [AdminFiltersDispatchTypes.SetAppointments]: AdminFiltersContextState['appointments'];
    [AdminFiltersDispatchTypes.Reinitialize]: never;
}

/**
 * The union type of dispatch actions that results from using {@link ActionMap}.
 */
export type AdminFiltersActions = ActionMap<AdminFiltersPayload>[keyof ActionMap<AdminFiltersPayload>];

/**
 * The admin reducer function type definition. Primarily used by any `useReducer`
 * that uses the function or the function itself.
 */
export type AdminFiltersReducerFunction = GoMotoFunction<
    [AdminFiltersContextState, AdminFiltersActions],
    AdminFiltersContextState
>;
// #endregion

// #region Provider
/**
 * The object types for the props of the admin provider component.
 */
export interface AdminFiltersProviderProps {
    tabType: TabType;
    /**
     * Any element that will render as a child of the admin context.
     */
    children?: ReactNode;
}
// #endregion

// #region Hooks
/**
 * The function type for the `useAdminFiltersState` hook. Takes no arguments and returns the current admin state.
 */
export type AdminFiltersStateHook = GoMotoFunction<
    [void],
    AdminFiltersContextState
>;

/**
 * The function type for the `useAdminFiltersDispatch` hook. Takes no arguments and returns a dispatch function for the admin context.
 */
export type AdminFiltersDispatchHook = GoMotoFunction<
    [void],
    AdminFiltersDispatchState
>;

/**
 * An object of all returned props from the `useAdminFiltersFetchAppointments` hook.
 */
export interface AdminFiltersFetchAppointmentsReturnProps {
    /**
     * Function to retrieve all relevant appointments based on filters and which tab is being displayed.
     */
    fetchAppointments: GoMotoFunction<
        [TabType],
        void
    >;
}

/**
 * The function type for the `useAdminFiltersFetchAppointments` hook. Takes no arguments and returns a dispatch function for the admin context.
 */
export type AdminFiltersFetchAppointmentsHook = GoMotoFunction<
    [void],
    AdminFiltersFetchAppointmentsReturnProps
>;
// #endregion